import { useParams } from "react-router-dom";
import { Confirmation, Error, Reason, Success } from "../../Components/Modal";
import { RejectDriverSignup } from "../../Components/Modal/RejectDriverSignup";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
    changeSignupRequestStatus,
    getSignupRequestDetails,
} from "../../Services/SignupRequest";
import Button from "../../Components/Button";
import { format_date, resolveImageUrl } from "../../Utils/helpers";
// Checkr disabled — not using background check service
// import axios from "axios";
// import { connection_string } from "../../Utils/connection_strings";
import BackButton from "../../Components/BackButton";
import ModalImageViewer from "../../Components/ModalImageViewer";
import "../Dashboard/Dashboard.css";

const AVATAR_FALLBACK = "images/avatar.png";

const getRequestStatusBadgeClass = (status) => {
    const value = status?.toLowerCase();
    if (value === "approved") return "signup-request-status-badge is-approved";
    if (value === "rejected") return "signup-request-status-badge is-rejected";
    return "signup-request-status-badge is-pending";
};

const formatRequestStatus = (status, reverification = false) => {
    if (!status) return "Pending";
    const label = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    if (reverification && (status === "pending" || status === "rejected")) {
        return status === "pending" ? "Re-verification" : "Re-submit Required";
    }
    return label;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const getExpiryInfo = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) {
        return { state: "missing", text: "Not provided", pill: { label: "Missing", className: "verify-pill is-unverified" } };
    }
    const daysLeft = Math.ceil((date.getTime() - Date.now()) / DAY_MS);
    const text = format_date(date, "DD/MM/YYYY");
    if (daysLeft <= 0) return { state: "expired", text, pill: { label: "Expired", className: "verify-pill is-unverified" } };
    if (daysLeft <= 30) return { state: "soon", text, pill: { label: `${daysLeft}d left`, className: "verify-pill is-unverified" } };
    return { state: "valid", text, pill: { label: "Valid", className: "verify-pill is-verified" } };
};

export default function SignupRequestDetails() {
    // const sampleData = [
    //   {
    //     sn: "1",
    //     bookingId: "123",
    //     moverName: "Mark Carson",
    //     date: "01/02/2022",
    //     pickupLocation: "Abc Location",
    //     dropoffLocation: "Abc Location",
    //     status: "pending",
    //   },
    //   {
    //     sn: "2",
    //     bookingId: "123",
    //     moverName: "Mark Carson",
    //     date: "01/02/2022",
    //     pickupLocation: "Abc Location",
    //     dropoffLocation: "Abc Location",
    //     status: "accepted",
    //   },
    //   {
    //     sn: "3",
    //     bookingId: "123",
    //     moverName: "Mark Carson",
    //     date: "01/02/2022",
    //     pickupLocation: "Abc Location",
    //     dropoffLocation: "Abc Location",
    //     status: "completed",
    //   },
    //   {
    //     sn: "4",
    //     bookingId: "123",
    //     moverName: "Mark Carson",
    //     date: "01/02/2022",
    //     pickupLocation: "Abc Location",
    //     dropoffLocation: "Abc Location",
    //     status: "cancelled",
    //   },
    // ];

    const { id } = useParams();
    const [requestDetail, setRequestDetail] = useState({});
    // Checkr disabled — not using background check service
    // const [packageName, setPackageName] = useState("");
    // const [isInvitationSend, setIsInvitationSend] = useState(false);
    const [profileImageSrc, setProfileImageSrc] = useState(AVATAR_FALLBACK);

    const [viewerShow, setViewerShow] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [viewerTitle, setViewerTitle] = useState("");

    const openImageViewer = (imagesList, index = 0, title = "Image Viewer") => {
        setViewerImages(imagesList);
        setViewerIndex(index);
        setViewerTitle(title);
        setViewerShow(true);
    };

    const { data, refetch } = useQuery(
        ["signupRequest_details", id],
        () => getSignupRequestDetails(id)
    );

    useEffect(() => {
        if (data?.data?.data)
            setRequestDetail(data?.data?.data);
    }, [data]);

    useEffect(() => {
        setProfileImageSrc(
            resolveImageUrl(requestDetail?.user?.profileImage, AVATAR_FALLBACK)
        );
    }, [requestDetail?.user?.profileImage]);

    const user = requestDetail?.user || {};
    const vehicle = user?.vehicle || requestDetail?.vehicle || null;
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Driver";
    const licenseFrontUrl = resolveImageUrl(user?.licenseImageFront);
    const licenseBackUrl = resolveImageUrl(user?.licenseImageBack);
    const insuranceUrl = resolveImageUrl(user?.insurance);
    const isPhoneVerified = Boolean(user?.phoneVerified);
    const isEmailVerified = Boolean(user?.emailVerified);
    const canReviewRequest = requestDetail?.status === "pending" && isPhoneVerified;
    const vehicleImageUrls = (Array.isArray(vehicle?.vehicleImage) ? vehicle.vehicleImage : [])
        .map((img) => resolveImageUrl(img))
        .filter(Boolean);

    const licenseExpiry = getExpiryInfo(user?.licenseExpiryDate);
    const insuranceExpiry = getExpiryInfo(vehicle?.insuranceExpiryDate);
    const registrationExpiry = getExpiryInfo(vehicle?.registrationExpiryDate);
    const expiryChecks = [
        { label: "Driver license", field: "licenseExpiryDate", info: licenseExpiry },
        ...(vehicle ? [
            { label: "Vehicle insurance", field: "insuranceExpiryDate", info: insuranceExpiry },
            { label: "Vehicle registration", field: "registrationExpiryDate", info: registrationExpiry },
        ] : []),
    ];
    const expiredDocs = expiryChecks.filter((c) => c.info.state === "expired").map((c) => c.label);
    const missingDocs = expiryChecks.filter((c) => c.info.state === "missing").map((c) => c.label);
    const expiryFieldsToFix = expiryChecks
        .filter((c) => c.info.state === "expired" || c.info.state === "missing")
        .map((c) => c.field);

    const handleApprove = () => {
        if (expiredDocs.length) {
            Error(`Cannot approve: ${expiredDocs.join(", ")} already expired. Use "Request updates" so the driver uploads a valid document.`);
            return;
        }
        const warning = missingDocs.length
            ? `Expiry date missing for: ${missingDocs.join(", ")}. The driver will not get expiry reminders until you add the dates from Driver Details. Approve anyway?`
            : "Are you sure you want to approve this signup request?";
        Confirmation(warning, "Yes", () => mutate({ status: "approved" }));
    };

    const statItems = [
        {
            label: "Phone Number",
            value: user?.phone || "N/A",
            verify: {
                label: isPhoneVerified ? "Verified" : "Unverified",
                className: isPhoneVerified ? "verify-pill is-verified" : "verify-pill is-unverified",
            },
        },
        {
            label: "Email Address",
            value: user?.email || "N/A",
            verify: {
                label: isEmailVerified ? "Verified" : "Unverified",
                className: isEmailVerified ? "verify-pill is-verified" : "verify-pill is-unverified",
            },
        },
        { label: "SSN", value: user?.ssn || "N/A" },
        {
            label: "Date of Birth",
            value: user?.dob ? format_date(user.dob, "DD/MM/YYYY") : "N/A",
        },
        { label: "Zipcode", value: user?.zipcode || "N/A" },
        { label: "License State", value: user?.driverLicenseState || "N/A" },
        { label: "License Number", value: user?.driverLicenseNumber || "N/A" },
        { label: "License Expiry", value: licenseExpiry.text, verify: licenseExpiry.pill },
        {
            label: "Request Date",
            value: requestDetail?.createdAt
                ? format_date(requestDetail.createdAt, "DD/MM/YYYY")
                : "N/A",
        },
    ];

    const handleProfileImageError = () => {
        setProfileImageSrc(AVATAR_FALLBACK);
    };

    const openProfileViewer = () => {
        if (profileImageSrc && profileImageSrc !== AVATAR_FALLBACK) {
            openImageViewer([profileImageSrc], 0, "Profile Photo");
        }
    };
    const { mutate, isLoading: loadingStatus } = useMutation(
        (data) => changeSignupRequestStatus(id, data),
        {
            onSuccess: (res) => {
                refetch();
                Success("Signup Request Status", res.data?.message);
            },
            onError: (err) => {
                Error(err?.response?.data?.message);
            },
        }
    );

    // Checkr disabled — not using background check service
    // const sendInvitation = async () => {
    //     if (packageName) {
    //         try {
    //             const endpointUrl = connection_string + '/user/send-invitation';
    //             const data = {
    //                 candidate_id: requestDetail?.user?.candidateId,
    //                 user_id: requestDetail?.user?._id,
    //                 package: packageName,
    //                 country: requestDetail?.user?.country,
    //                 city: requestDetail?.user?.city,
    //                 state: requestDetail?.user?.driverLicenseState
    //             };
    //             console.log(data);
    //             var sendInvitation = await axios.post(
    //                 endpointUrl,
    //                 data,
    //                 {
    //                     headers: {
    //                         'Content-Type': 'application/json',
    //                     },
    //                 }
    //             );
    //
    //             setIsInvitationSend(true);
    //
    //             console.log(sendInvitation);
    //
    //         }
    //         catch (error) {
    //             console.log(error);
    //         }
    //     }
    // }

    return (
        <div className="app-content content dashboard">
            <div className="content-wrapper">
                <div className="content-body">
                    {/* Basic form layout section start */}
                    <section id="configuration">
                        <div className="row">
                            <div className="col-12">
                                <div className="user-details-wrapper">
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <div className="back-title d-flex flex-wrap align-items-center justify-content-between gap-3">
                                                <div className="d-flex align-items-center">
                                                    <BackButton />
                                                    <h1 className="pageTitle m-0 ms-3 fw-800 fs-2">
                                                        Driver Request Details
                                                    </h1>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row mb-4 mb-lg-5">
                                        <div className="col-12">
                                            <div className="user-profile-card details-card-premium">
                                                <div className="user-profile-banner" />
                                                <div className="user-profile-body">
                                                    <div className="user-profile-top d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 gap-md-4">
                                                        <button
                                                            type="button"
                                                            className="user-profile-avatar-btn"
                                                            onClick={openProfileViewer}
                                                            aria-label="View profile photo"
                                                        >
                                                            <div className="user-profile-avatar-wrap">
                                                                <img
                                                                    src={profileImageSrc}
                                                                    alt={fullName}
                                                                    className="userImage"
                                                                    onError={handleProfileImageError}
                                                                />
                                                            </div>
                                                        </button>

                                                        <div className="user-profile-meta flex-grow-1 text-center text-md-start w-100">
                                                            <div className="d-flex flex-column flex-sm-row align-items-center align-items-sm-start justify-content-between gap-2 gap-sm-3 mb-2">
                                                                <div>
                                                                    <h2 className="user-profile-name text-capitalize mb-1">
                                                                        {fullName}
                                                                    </h2>
                                                                    <p className="user-profile-email mb-0">{user?.email}</p>
                                                                    {requestDetail?.createdAt && (
                                                                        <p className="user-profile-member mb-0">
                                                                            Requested on {format_date(requestDetail.createdAt, "DD/MM/YYYY")}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <div className="driver-profile-badges">
                                                                    <span className={getRequestStatusBadgeClass(requestDetail?.status)}>
                                                                        {formatRequestStatus(
                                                                            requestDetail?.status,
                                                                            requestDetail?.reverification
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="user-profile-stats">
                                                        {statItems.map((item) => (
                                                            <div key={item.label} className="user-profile-stat">
                                                                <span className="user-profile-stat-label">{item.label}</span>
                                                                <div className="user-profile-stat-value-row">
                                                                    <span className="user-profile-stat-value">{item.value}</span>
                                                                    {item.verify && (
                                                                        <span className={item.verify.className}>
                                                                            {item.verify.label}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="driver-docs-section">
                                                        <h3 className="driver-docs-title">Submitted Documents</h3>
                                                        <div className="row g-3">
                                                            <div className="col-sm-6 col-lg-4">
                                                                {licenseFrontUrl ? (
                                                                    <button
                                                                        type="button"
                                                                        className="driver-doc-card w-100 text-start"
                                                                        onClick={() => openImageViewer(
                                                                            [licenseFrontUrl, licenseBackUrl].filter(Boolean),
                                                                            0,
                                                                            "Driver License"
                                                                        )}
                                                                    >
                                                                        <img src={licenseFrontUrl} alt="License front" />
                                                                        <span className="driver-doc-card__label">License Front</span>
                                                                    </button>
                                                                ) : (
                                                                    <div className="driver-doc-empty">No front license image</div>
                                                                )}
                                                            </div>
                                                            <div className="col-sm-6 col-lg-4">
                                                                {licenseBackUrl ? (
                                                                    <button
                                                                        type="button"
                                                                        className="driver-doc-card w-100 text-start"
                                                                        onClick={() => openImageViewer(
                                                                            [licenseFrontUrl, licenseBackUrl].filter(Boolean),
                                                                            licenseFrontUrl ? 1 : 0,
                                                                            "Driver License"
                                                                        )}
                                                                    >
                                                                        <img src={licenseBackUrl} alt="License back" />
                                                                        <span className="driver-doc-card__label">License Back</span>
                                                                    </button>
                                                                ) : (
                                                                    <div className="driver-doc-empty">No back license image</div>
                                                                )}
                                                            </div>
                                                            <div className="col-sm-6 col-lg-4">
                                                                {insuranceUrl ? (
                                                                    <button
                                                                        type="button"
                                                                        className="driver-doc-card w-100 text-start"
                                                                        onClick={() => openImageViewer([insuranceUrl], 0, "Insurance Document")}
                                                                    >
                                                                        <img src={insuranceUrl} alt="Insurance" />
                                                                        <span className="driver-doc-card__label">Insurance</span>
                                                                    </button>
                                                                ) : (
                                                                    <div className="driver-doc-empty">No insurance document</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {requestDetail?.status === "rejected" && (
                                                        <div className="alert alert-danger mt-3 mb-0" role="alert">
                                                            {requestDetail?.reason ? (
                                                                <div className="mb-2">
                                                                    <strong>Rejection Reason:</strong> {requestDetail.reason}
                                                                </div>
                                                            ) : null}
                                                            {Array.isArray(requestDetail?.rejectedFields) &&
                                                            requestDetail.rejectedFields.length > 0 ? (
                                                                <div>
                                                                    <strong>Flagged items:</strong>
                                                                    <ul className="mb-0 mt-1">
                                                                        {requestDetail.rejectedFields.map((item) => (
                                                                            <li key={item.field}>
                                                                                {item.label || item.field}
                                                                                {item.reason ? ` — ${item.reason}` : ""}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    )}

                                                    <div className="driver-docs-block mt-4">
                                                        <h3 className="sectionTitle mb-3">Vehicle Details</h3>
                                                        {!vehicle ? (
                                                            <div className="alert alert-warning mb-0" role="alert">
                                                                No vehicle submitted with this signup request.
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="user-profile-stats mb-3">
                                                                    {[
                                                                        { label: "Vehicle Name", value: vehicle?.name || "N/A" },
                                                                        { label: "Type", value: vehicle?.type?.name || vehicle?.type?.title || "N/A" },
                                                                        { label: "Brand", value: vehicle?.brandName || "N/A" },
                                                                        { label: "Year", value: vehicle?.year || "N/A" },
                                                                        { label: "Color", value: vehicle?.color || "N/A" },
                                                                        { label: "Plate No", value: vehicle?.numberPlate || "N/A" },
                                                                        { label: "Insurance Expiry", value: insuranceExpiry.text, verify: insuranceExpiry.pill },
                                                                        { label: "Registration Expiry", value: registrationExpiry.text, verify: registrationExpiry.pill },
                                                                        {
                                                                            label: "Vehicle Status",
                                                                            value: formatRequestStatus(vehicle?.status),
                                                                        },
                                                                    ].map((item) => (
                                                                        <div key={item.label} className="user-profile-stat">
                                                                            <span className="user-profile-stat-label">{item.label}</span>
                                                                            <div className="user-profile-stat-value-row">
                                                                                <span className="user-profile-stat-value">{item.value}</span>
                                                                                {item.verify && (
                                                                                    <span className={item.verify.className}>{item.verify.label}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                                {vehicle?.rejectedReason ? (
                                                                    <div className="alert alert-danger mb-3" role="alert">
                                                                        <strong>Vehicle Rejection:</strong> {vehicle.rejectedReason}
                                                                    </div>
                                                                ) : null}
                                                                <div className="row g-3">
                                                                    {vehicleImageUrls.length ? (
                                                                        vehicleImageUrls.map((url, index) => (
                                                                            <div className="col-sm-6 col-lg-4" key={`${url}-${index}`}>
                                                                                <button
                                                                                    type="button"
                                                                                    className="driver-doc-card w-100 text-start"
                                                                                    onClick={() =>
                                                                                        openImageViewer(
                                                                                            vehicleImageUrls,
                                                                                            index,
                                                                                            "Vehicle Images"
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <img src={url} alt={`Vehicle ${index + 1}`} />
                                                                                    <span className="driver-doc-card__label">
                                                                                        Vehicle Image {index + 1}
                                                                                    </span>
                                                                                </button>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <div className="col-12">
                                                                            <div className="driver-doc-empty">No vehicle images</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>

                                                    <div className="driver-request-actions">
                                                        {canReviewRequest && (
                                                            <div className="driver-request-actions__btns">
                                                                <Button
                                                                    type="button"
                                                                    className="greenBtn"
                                                                    loading={loadingStatus}
                                                                    onClick={handleApprove}
                                                                >
                                                                    Approve Request
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    className="redBtn"
                                                                    loading={loadingStatus}
                                                                    onClick={() => {
                                                                        RejectDriverSignup(
                                                                            ({ reason, rejectedFields }) =>
                                                                                mutate({
                                                                                    status: "rejected",
                                                                                    reason,
                                                                                    rejectedFields,
                                                                                }),
                                                                            { preselectedFields: expiryFieldsToFix }
                                                                        );
                                                                    }}
                                                                >
                                                                    Request updates
                                                                </Button>
                                                            </div>
                                                        )}

                                                        {requestDetail?.status === "pending" && (expiredDocs.length > 0 || missingDocs.length > 0) && (
                                                            <div className="driver-request-note alert alert-warning mb-0" role="alert">
                                                                {expiredDocs.length > 0 && (
                                                                    <div><strong>Expired:</strong> {expiredDocs.join(", ")} - approval is blocked until the driver re-submits.</div>
                                                                )}
                                                                {missingDocs.length > 0 && (
                                                                    <div><strong>Expiry date missing:</strong> {missingDocs.join(", ")} (older app version). Add the dates from Driver Details after approval.</div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {requestDetail?.status === "pending" && !isPhoneVerified && (
                                                            <div className="driver-request-note alert alert-warning mb-0" role="alert">
                                                                <strong>Phone Unverified:</strong> Approve/Reject is locked until the driver verifies their phone number.
                                                            </div>
                                                        )}

                                                        {requestDetail?.status === "pending" && isPhoneVerified && !vehicle && (
                                                            <div className="driver-request-note alert alert-warning mb-0" role="alert">
                                                                <strong>Vehicle Missing:</strong> This driver has not submitted vehicle details yet.
                                                                Prefer requesting vehicle at signup for new drivers. You can still approve; they may add a vehicle after login.
                                                            </div>
                                                        )}

                                                        {/* Checkr disabled — not using background check service
                                                        {!user?.invitationId ? (
                                                            <div className="driver-invite-wrap d-flex flex-wrap align-items-center gap-2 ms-lg-auto">
                                                                <label className="user-profile-stat-label mb-0">Send Invitation</label>
                                                                <select
                                                                    name="packageName"
                                                                    className="dashInput driver-invite-select"
                                                                    onChange={(e) => setPackageName(e.target.value)}
                                                                    defaultValue=""
                                                                >
                                                                    <option value="" disabled>Select package type</option>
                                                                    <option value="test_pro_criminal_and_mvr">Test Pro Criminal and MVR</option>
                                                                    <option value="test_mvr_only">Test MVR Only</option>
                                                                </select>
                                                                {packageName && !isInvitationSend && (
                                                                    <Button
                                                                        type="button"
                                                                        className="siteBtn"
                                                                        onClick={sendInvitation}
                                                                    >
                                                                        Send
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="ms-lg-auto">
                                                                {user?.reportId && user?.reportStatus === "completed" ? (
                                                                    <div className="driver-checkr-result">
                                                                        <p className="mb-1 small">
                                                                            Checkr Result: <strong>{user?.reportResult}</strong>
                                                                        </p>
                                                                        <p className="mb-0 small">
                                                                            Assessment: <strong>{user?.reportAssessment}</strong>
                                                                        </p>
                                                                    </div>
                                                                ) : (
                                                                    <span className="badge bg-info p-2 px-3 rounded-pill">
                                                                        <i className="fas fa-clock me-2" />
                                                                        Checkr Report Pending
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        */}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* <Table
                    key={"signup-request-details-table"}
                    tableTitles={[
                      "S.No.",
                      "Booking ID",
                      "Driver Name",
                      "Date",
                      "Pickup Location",
                      "Drop-Off Location",
                      "Status",
                      // "Action",
                    ]}
                    dateFilter
                    showEntries
                    sortBy
                    // isLoading={isLoading}
                    // isFetching={isFetching}
                    // data={data?.data?.users}
                    // totalPages={data?.data?.totalPages}
                    // totalDocs={data?.data?.totalDocs}
                    // setPage={setPage}
                    // setSearchString={setSearchString}
                    // status={status}
                    // setStatus={setStatus}
                    // perPage={perPage}
                    // setPerPage={setPerPage}
                    // from={from}
                    // setFrom={setFrom}
                    // to={to}
                    // setTo={setTo}
                  >
                    {sampleData?.map((item, index) => (
                      <tr>
                        <td>{item.sn}</td>
                        <td>{item.bookingId}</td>
                        <td>{item.moverName}</td>
                        <td>{item.date}</td>
                        <td>{item.pickupLocation}</td>
                        <td>{item.dropoffLocation}</td>
                        <td>{item.status}</td>
                      </tr>
                    ))}
                  </Table> */}

                                    {/* <div className="row mb-3">
                    <div className="col-12">
                      <div className="dataTables_length text-start mb-4">
                        <div className="user-listing-top dashCard2 py-4 px-4">
                          <div className="d-xxl-flex align-items-end justify-content-between mb-xxl-3">
                            <div className="userInput mb-3">
                              <div className="d-md-flex align-items-center">
                                <label
                                  htmlFor=""
                                  className="dashLabel mb-3 mb-xxl-0 me-3"
                                >
                                  Sort By Date
                                </label>
                                <div className="d-sm-flex">
                                  <div className="mb-2 mb-xxl-0 me-sm-3">
                                    <input
                                      className="dashInput p-3"
                                      type="date"
                                    />
                                  </div>
                                  <div className="mb-2 mb-xxl-0">
                                    <input
                                      className="dashInput p-3"
                                      type="date"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="userInput mb-2">
                              <div className="form-field mb-3">
                                <div className="d-xxl-flex align-items-center justify-content-end">
                                  <div className="dataTables_filter">
                                    <div className="dashSearchWrap">
                                      <form action="" method="POST">
                                        <input
                                          type="search"
                                          id="search-inp"
                                          className="dashInput search-input w-100"
                                          placeholder="Search"
                                        />
                                        <button
                                          type="submit"
                                          className="search-icon"
                                        >
                                          <i className="fas fa-search" />
                                        </button>
                                      </form>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="d-xxl-flex align-items-end flex-wrap justify-content-between mb-xxl-3">
                            <div className="userInput mb-3">
                              <label className="dashLabel d-inline-block mb-3 mb-xxl-0 me-3">
                                Show
                              </label>
                              <select className="d-inline-block dashInput sm-dropdown">
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                              </select>
                              <label className="dashLabel d-inline-block mb-3 mb-xxl-0 ms-3">
                                Entries
                              </label>
                            </div>
                            <div className="userInput mb-3">
                              <label className="dashLabel d-inline-block mb-3 mb-xxl-0">
                                Filter By:
                              </label>
                              <select className="d-inline-block dashInput">
                                <option
                                  value=""
                                  disabled=""
                                  hidden=""
                                  selected=""
                                >
                                  All
                                </option>
                                <option value={1}>Active</option>
                                <option value={2}>Inactive</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="maain-tabble table-responsive">
                        <table className="table table-bordered zero-configuration">
                          <thead>
                            <tr>
                              <th>S No.</th>
                              <th>Booking ID</th>
                              <th>Driver Name</th>
                              <th>Date</th>
                              <th>Pickup Location</th>
                              <th>Drop-Off Location</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>1</td>
                              <td>123</td>
                              <td>Daniel Jacob</td>
                              <td>mm/dd/yyyy</td>
                              <td>Abc Location</td>
                              <td>ABC Location</td>
                              <td>Pending</td>
                              <td>
                                <div className="btn-group">
                                  <button
                                    type="button"
                                    className="btn transparent-btn ellipsis-btn"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                  >
                                    {" "}
                                    <i className="fa fa-ellipsis-v" />
                                  </button>
                                  <div className="dropdown-menu text-left custom-dropdown">
                                    <a
                                      className="dropdown-item"
                                      href="view-signup-requests.php"
                                    >
                                      <i className="far fa-eye" />
                                      View
                                    </a>
                                    <button
                                      type="button"
                                      className="dropdown-item notBtn"
                                      data-bs-toggle="modal"
                                      data-bs-target="#approveSignupRequest"
                                    >
                                      <i className="fas fa-check" />
                                      Accept
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>2</td>
                              <td>123</td>
                              <td>Daniel Jacob</td>
                              <td>mm/dd/yyyy</td>
                              <td>Abc Location</td>
                              <td>ABC Location</td>
                              <td>Accepted</td>
                              <td>
                                <div className="btn-group">
                                  <button
                                    type="button"
                                    className="btn transparent-btn ellipsis-btn"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                  >
                                    {" "}
                                    <i className="fa fa-ellipsis-v" />
                                  </button>
                                  <div className="dropdown-menu text-left custom-dropdown">
                                    <a
                                      className="dropdown-item"
                                      href="view-signup-requests.php"
                                    >
                                      <i className="far fa-eye" />
                                      View
                                    </a>
                                    <button
                                      type="button"
                                      className="dropdown-item notBtn"
                                      data-bs-toggle="modal"
                                      data-bs-target="#approveSignupRequest"
                                    >
                                      <i className="fas fa-check" />
                                      Accept
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>3</td>
                              <td>123</td>
                              <td>Daniel Jacob</td>
                              <td>mm/dd/yyyy</td>
                              <td>Abc Location</td>
                              <td>ABC Location</td>
                              <td>Completed</td>
                              <td>
                                <div className="btn-group">
                                  <button
                                    type="button"
                                    className="btn transparent-btn ellipsis-btn"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                  >
                                    {" "}
                                    <i className="fa fa-ellipsis-v" />
                                  </button>
                                  <div className="dropdown-menu text-left custom-dropdown">
                                    <a
                                      className="dropdown-item"
                                      href="view-signup-requests.php"
                                    >
                                      <i className="far fa-eye" />
                                      View
                                    </a>
                                    <button
                                      type="button"
                                      className="dropdown-item notBtn"
                                      data-bs-toggle="modal"
                                      data-bs-target="#approveSignupRequest"
                                    >
                                      <i className="fas fa-check" />
                                      Accept
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>4</td>
                              <td>123</td>
                              <td>Daniel Jacob</td>
                              <td>mm/dd/yyyy</td>
                              <td>Abc Location</td>
                              <td>ABC Location</td>
                              <td>Cancelled</td>
                              <td>
                                <div className="btn-group">
                                  <button
                                    type="button"
                                    className="btn transparent-btn ellipsis-btn"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                  >
                                    {" "}
                                    <i className="fa fa-ellipsis-v" />
                                  </button>
                                  <div className="dropdown-menu text-left custom-dropdown">
                                    <a
                                      className="dropdown-item"
                                      href="view-signup-requests.php"
                                    >
                                      <i className="far fa-eye" />
                                      View
                                    </a>
                                    <button
                                      type="button"
                                      className="dropdown-item notBtn"
                                      data-bs-toggle="modal"
                                      data-bs-target="#approveSignupRequest"
                                    >
                                      <i className="fas fa-check" />
                                      Accept
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>5</td>
                              <td>123</td>
                              <td>Daniel Jacob</td>
                              <td>mm/dd/yyyy</td>
                              <td>Abc Location</td>
                              <td>ABC Location</td>
                              <td>Pending</td>
                              <td>
                                <div className="btn-group">
                                  <button
                                    type="button"
                                    className="btn transparent-btn ellipsis-btn"
                                    data-bs-toggle="dropdown"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                  >
                                    {" "}
                                    <i className="fa fa-ellipsis-v" />
                                  </button>
                                  <div className="dropdown-menu text-left custom-dropdown">
                                    <a
                                      className="dropdown-item"
                                      href="view-signup-requests.php"
                                    >
                                      <i className="far fa-eye" />
                                      View
                                    </a>
                                    <button
                                      type="button"
                                      className="dropdown-item notBtn"
                                      data-bs-toggle="modal"
                                      data-bs-target="#rejectSignupRequest"
                                    >
                                      <i className="fas fa-times" />
                                      Reject
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="row align-items-center">
                    <div className="col-sm-12 col-md-5">
                      <div
                        className="showing-result"
                        id="DataTables_Table_0_info"
                      >
                        Showing 10 of 50 Entries
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-7">
                      <div
                        className="dataTables_paginate paging_simple_numbers"
                        id="DataTables_Table_0_paginate"
                      >
                        <ul className="pagination justify-content-end">
                          <li className="paginate_button page-item previous">
                            <button type="button" className="page-link notBtn">
                              Previous
                            </button>
                          </li>
                          <li className="paginate_button page-item active">
                            <button type="button" className="page-link notBtn">
                              01
                            </button>
                          </li>
                          <li className="paginate_button page-item">
                            <button type="button" className="page-link notBtn">
                              02
                            </button>
                          </li>
                          <li className="paginate_button page-item">
                            <button type="button" className="page-link notBtn">
                              03
                            </button>
                          </li>
                          <li className="paginate_button page-item next">
                            <button type="button" className="page-link notBtn">
                              Next
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div> */}
                                </div>
                            </div>
                        </div>
                        {/* <div
              className="modal fade"
              id="approveSignupRequest"
              data-bs-backdrop="static"
              role="dialog"
              aria-labelledby="exampleModalCenterTitle"
            >
              <div
                className="modal-dialog modal-dialog-centered site-modal"
                role="document"
              >
                <div className="modal-content">
                  <div className="modalIndex">
                    <i
                      className="fas fa-times close modal-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    />
                    <div className="text-center">
                      <div className="modal-text-content mb-1">
                        <div className="modalImg">
                          <img
                            src="images/question.png"
                            alt=""
                            className="img-fluid"
                          />
                        </div>
                        <div className="modal-text-content my-4">
                          <p className="modalText">
                            Are you sure you want to approve the signup request
                            ?
                          </p>
                        </div>
                      </div>
                      <div className="modalAction">
                        <button
                          type="button"
                          data-bs-dismiss="modal"
                          className="siteBtn2 mx-1"
                        >
                          No
                        </button>
                        <button
                          type="button"
                          data-bs-dismiss="modal"
                          data-bs-toggle="modal"
                          data-bs-target="#approvedSignupRequest"
                          className="siteBtn mx-1"
                        >
                          Yes
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="modal fade"
              id="approvedSignupRequest"
              data-bs-backdrop="static"
              role="dialog"
              aria-labelledby="exampleModalCenterTitle"
            >
              <div
                className="modal-dialog modal-dialog-centered site-modal"
                role="document"
              >
                <div className="modal-content">
                  <div className="modalIndex">
                    <i
                      className="fas fa-times close modal-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    />
                    <div className="text-center">
                      <div className="modal-text-content mb-1">
                        <div className="modalImg">
                          <img
                            src="images/check.png"
                            alt=""
                            className="img-fluid"
                          />
                        </div>
                        <div className="modal-text-content my-4">
                          <p className="modalText">
                            Signup request has been approved successfully
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="modal fade"
              id="rejectSignupRequest"
              data-bs-backdrop="static"
              role="dialog"
              aria-labelledby="exampleModalCenterTitle"
            >
              <div
                className="modal-dialog modal-dialog-centered site-modal"
                role="document"
              >
                <div className="modal-content">
                  <div className="modalIndex">
                    <i
                      className="fas fa-times close modal-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    />
                    <div className="text-center">
                      <div className="modal-text-content mb-1">
                        <div className="modalImg">
                          <img
                            src="images/question.png"
                            alt=""
                            className="img-fluid"
                          />
                        </div>
                        <div className="modal-text-content my-4">
                          <p className="modalText">
                            Are you sure you want to reject the signup request ?
                          </p>
                        </div>
                      </div>
                      <form action="">
                        <div className="modalForm">
                          <div className="text-center">
                            <h3 className="sectionTitle">Reject Reason</h3>
                          </div>
                          <textarea
                            name=""
                            id=""
                            rows={4}
                            className="modalInput"
                            defaultValue={""}
                          />
                        </div>
                        <div className="modalAction my-3">
                          <button
                            type="button"
                            data-bs-dismiss="modal"
                            className="siteBtn2 mx-1"
                          >
                            No
                          </button>
                          <button
                            type="button"
                            data-bs-dismiss="modal"
                            data-bs-toggle="modal"
                            data-bs-target="#rejectedSignupRequest"
                            className="siteBtn mx-1"
                          >
                            Yes
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="modal fade"
              id="rejectedSignupRequest"
              data-bs-backdrop="static"
              role="dialog"
              aria-labelledby="exampleModalCenterTitle"
            >
              <div
                className="modal-dialog modal-dialog-centered site-modal"
                role="document"
              >
                <div className="modal-content">
                  <div className="modalIndex">
                    <i
                      className="fas fa-times close modal-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    />
                    <div className="text-center">
                      <div className="modal-text-content mb-1">
                        <div className="modalImg">
                          <img
                            src="images/times.png"
                            alt=""
                            className="img-fluid"
                          />
                        </div>
                        <div className="modal-text-content my-4">
                          <p className="modalText">
                            Signup request has been rejected
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
                    </section>

                    <ModalImageViewer
                        show={viewerShow}
                        images={viewerImages}
                        startIndex={viewerIndex}
                        onHide={() => setViewerShow(false)}
                        title={viewerTitle}
                    />
                </div>
            </div>
        </div>
    );
}
